import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API_URL } from "../../config.js";
import Notification from "../../components/Notification.js";
import FarmersDropdownField from "../../components/FarmersDropdownField.js";
import ConfirmDialog from "../../components/ConfirmDialog.js";
import { getLocalDate } from "../../utils/TextUtils.js";

function DebtRepaymentForm() {
    const [repayment, setRepayment] = useState({
        farmer_id: "",
        amount_paid: "",
        payment_date: getLocalDate(),
        notes: ""
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [allFarmers, setAllFarmers] = useState([]);
    const [farmerBalanceDue, setFarmerBalanceDue] = useState(null);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    
    const navigate = useNavigate();
    const token = localStorage.getItem("access_token");

    // Fetch all farmers on component mount
    useEffect(() => {
        const fetchAllFarmers = async () => {
            try {
                const response = await fetch(`${API_URL}/farmers?paginate=false`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (response.ok) {
                    const data = await response.json();
                    setAllFarmers(data || []);
                } else {
                    throw new Error("Failed to fetch farmers.");
                }
            } catch (error) {
                setNotification({ type: "danger", message: "An error occurred while fetching farmers." });
            }
        };

        fetchAllFarmers();
    }, [token]);

    // Fetch farmer's balance due when farmer is selected
    useEffect(() => {
        const fetchFarmerBalanceDue = async () => {
            if (!repayment.farmer_id) {
                setFarmerBalanceDue(null);
                return;
            }

            try {
                const response = await fetch(`${API_URL}/debt-repayments/farmer/${repayment.farmer_id}/summary`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (response.ok) {
                    const data = await response.json();
                    setFarmerBalanceDue(data.debt_summary.total_balance_due || 0);
                } else {
                    setFarmerBalanceDue(0);
                }
            } catch (error) {
                console.error("Error fetching farmer balance due:", error);
                setFarmerBalanceDue(0);
            }
        };

        fetchFarmerBalanceDue();
    }, [repayment.farmer_id, token]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setRepayment(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear errors when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Basic frontend validation
        if (!repayment.farmer_id || !repayment.amount_paid || !repayment.payment_date) {
            setNotification({
                type: "danger",
                message: "Please fill in all required fields."
            });
            return;
        }

        if (farmerBalanceDue === 0) {
            setNotification({
                type: "warning",
                message: "This farmer has no outstanding debt to repay."
            });
            return;
        }

        // Show confirmation dialog - backend will do the real validation
        setIsConfirmDialogOpen(true);
    };

    const confirmSubmit = async () => {
        setIsConfirmDialogOpen(false);
        setLoading(true);
        setErrors({}); // Clear previous errors

        try {
            const response = await fetch(`${API_URL}/debt-repayments`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(repayment),
            });

            const responseData = await response.json();

            if (response.ok) {
                setNotification({
                    type: "success",
                    message: "Debt repayment recorded successfully!"
                });

                // Reset form
                setRepayment({
                    farmer_id: "",
                    amount_paid: "",
                    payment_date: getLocalDate(),
                    notes: ""
                });
                setFarmerBalanceDue(null);

                // Redirect after success
                setTimeout(() => {
                    navigate("/dashboard/debt-repayments");
                }, 2000);

            } else if (response.status === 401) {
                navigate("/logout");
            } else if (response.status === 422) {
                // Validation errors from Laravel
                setErrors(responseData.errors || {});
                setNotification({
                    type: "danger",
                    message: "Please fix the errors in the form."
                });
            } else if (response.status === 400) {
                // Business logic errors (e.g., amount exceeds balance)
                setNotification({
                    type: "danger",
                    message: responseData.message || "Cannot proceed with repayment."
                });
            } else {
                setNotification({
                    type: "danger",
                    message: responseData.message || "An error occurred."
                });
            }

        } catch (error) {
            setNotification({
                type: "danger",
                message: "An error occurred while saving the repayment."
            });
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const cancelSubmit = () => {
        setIsConfirmDialogOpen(false);
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    // Check if submit button should be enabled
    const isSubmitDisabled = () => {
        if (loading) return true;
        if (!repayment.farmer_id || !repayment.amount_paid || !repayment.payment_date) return true;
        if (farmerBalanceDue === 0) return true;
        return false;
    };

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">Record Debt Repayment</p>
                    </div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/debt-repayments" className="button primary-bg">
                            <i className="fa fa-arrow-left" aria-hidden="true"></i> &nbsp; Back
                        </Link>
                    </p>
                </div>
            </nav>

            <div className="column dash-section">
                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                {/* Confirmation Dialog */}
                <ConfirmDialog
                    isOpen={isConfirmDialogOpen}
                    theme="is-primary"
                    title="Confirm Debt Repayment"
                    message={`Are you sure you want to record a repayment of KES ${repayment.amount_paid}? This will be distributed to the farmer's oldest debts.`}
                    onConfirm={confirmSubmit}
                    onCancel={cancelSubmit}
                    isLoading={loading}
                />

                <form onSubmit={handleSubmit}>
                    <div className="columns">
                        <div className="column is-half">
                            {/* Farmer Selection */}
                            <div className="field">
                                <label className="label">Farmer *</label>
                                <div className="control">
                                    <FarmersDropdownField
                                        name="farmer_id"
                                        value={repayment.farmer_id}
                                        onChange={handleChange}
                                        options={allFarmers}
                                        error={errors.farmer_id}
                                        placeholder="Select a farmer"
                                        required
                                    />
                                </div>
                                {errors.farmer_id && errors.farmer_id.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            {/* Farmer Balance Due Display */}
                            {farmerBalanceDue !== null && (
                                <div className={`box ${farmerBalanceDue > 0 ? 'has-background-danger-light' : 'has-background-success-light'}`}>
                                    <div className="content">
                                        <div className="columns is-vcentered">
                                            <div className="column">
                                                <p className="has-text-weight-bold">Balance Due:</p>
                                            </div>
                                            <div className="column has-text-right">
                                                <p className={`title is-4 ${farmerBalanceDue > 0 ? 'has-text-danger' : 'has-text-success'}`}>
                                                    KES {parseFloat(farmerBalanceDue).toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                        {farmerBalanceDue === 0 && (
                                            <p className="has-text-success has-text-weight-bold">
                                                <i className="fas fa-check-circle"></i> This farmer has no outstanding debt.
                                            </p>
                                        )}
                                        {farmerBalanceDue > 0 && (
                                            <p className="has-text-danger">
                                                <i className="fas fa-exclamation-circle"></i> This farmer has outstanding debt.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Amount to Pay */}
                            <div className="field">
                                <label className="label">Amount to Pay (KES) *</label>
                                <div className="control">
                                    <input
                                        className={`input ${errors.amount_paid ? "is-danger" : ""}`}
                                        type="number"
                                        name="amount_paid"
                                        value={repayment.amount_paid}
                                        onChange={handleChange}
                                        placeholder="Enter amount to pay"
                                        step="0.01"
                                        min="0.01"
                                        required
                                    />
                                </div>
                                {errors.amount_paid && errors.amount_paid.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            {/* Payment Date */}
                            <div className="field">
                                <label className="label">Payment Date *</label>
                                <div className="control">
                                    <input
                                        className={`input ${errors.payment_date ? "is-danger" : ""}`}
                                        type="date"
                                        name="payment_date"
                                        value={repayment.payment_date}
                                        onChange={handleChange}
                                        max={getLocalDate()}
                                        required
                                    />
                                </div>
                                {errors.payment_date && errors.payment_date.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            {/* Notes */}
                            <div className="field">
                                <label className="label">Notes (Optional)</label>
                                <div className="control">
                                    <textarea
                                        className="textarea"
                                        name="notes"
                                        value={repayment.notes}
                                        onChange={handleChange}
                                        placeholder="Add any notes about this repayment..."
                                        rows="3"
                                    />
                                </div>
                            </div>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                className="button primary-bg is-fullwidth"
                                disabled={isSubmitDisabled()}
                            >
                                {loading ? (
                                    <>
                                        <span className="icon">
                                            <i className="fas fa-spinner fa-spin"></i>
                                        </span>
                                        <span>PROCESSING...</span>
                                    </>
                                ) : farmerBalanceDue === 0 ? (
                                    "NO DEBT TO REPAY"
                                ) : (
                                    "RECORD REPAYMENT"
                                )}
                            </button>
                            
                            {farmerBalanceDue === 0 && (
                                <p className="help has-text-centered has-text-danger mt-2">
                                    <i className="fas fa-info-circle"></i> This farmer has no outstanding debt to repay.
                                </p>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </main>
    );
}

export default DebtRepaymentForm;